import {createLiveSessionScope} from "../context/createLiveSessionScope";
import { useEffect, useState } from "react";
import { isSensitiveCacheKey } from "../utils/aggregateCache";

export const ATTENDANCE_AUTH_MAX_AGE_MS = 30_000;
// Renew early enough that a renewal reaching its 12s limit still ends before
// the 30s confirmation lapses, so a slow network shows an error, not a stall.
export const ATTENDANCE_AUTH_REFRESH_MS = 15_000;

// Keep the existing forced Auth refresh and server-snapshot requirements.
// Focus is a refresh trigger, not a revocation of a still-current result.
function useTokenVerification(auth, uid, orgId, enabled = true) {
  const [validation, setValidation] = useState(null);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  useEffect(() => {
    if (!validation) return undefined;
    const expires = window.setTimeout(() => setValidation(current =>
      current?.at === validation.at ? null : current),
    Math.max(0, validation.at + ATTENDANCE_AUTH_MAX_AGE_MS - Date.now()));
    return () => window.clearTimeout(expires);
  }, [validation]);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true, epoch = 0, running = false, runningEpoch = 0, rerun = false;
    setValidation(null);
    setError(false);
    async function validate() {
      if (!active || navigator.onLine === false || !uid || !orgId) return;
      if (running) { rerun = runningEpoch !== epoch; return; }
      const user = auth.currentUser;
      if (user?.uid !== uid) { setValidation(null); return; }
      running = true;
      runningEpoch = epoch;
      const requestEpoch = epoch;
      // Stamp with the request START: an answer delayed by a suspended tab is
      // already old, so it can never restore access that has expired.
      const startedAt = Date.now();
      let timeout;
      try {
        await Promise.race([
          user.getIdToken(true),
          new Promise((_, reject) => {
            timeout = window.setTimeout(() => reject(new Error("authorization_timeout")), 12_000);
          }),
        ]);
        if (active && requestEpoch === epoch && auth.currentUser?.uid === uid && navigator.onLine !== false) {
          setValidation(Date.now() - startedAt < ATTENDANCE_AUTH_MAX_AGE_MS ? { uid, orgId, at: startedAt } : null);
          setError(false);
        }
      } catch {
        if (active && requestEpoch === epoch) { setValidation(null); setError(true); }
      } finally {
        window.clearTimeout(timeout);
        running = false;
        if (active && rerun) { rerun = false; validate(); }
      }
    }
    function disconnected() {
      epoch++;
      setOnline(false);
      setValidation(null);
    }
    function resumed() {
      setOnline(navigator.onLine !== false);
      if (navigator.onLine === false) { disconnected(); return; }
      validate();
    }
    // An installed app brought back on screen may not receive focus, and its
    // timers may have been paused, so re-verify as soon as it is visible.
    function visible() {
      if (document.visibilityState === "visible") resumed();
    }
    const refresh = window.setInterval(validate, ATTENDANCE_AUTH_REFRESH_MS);
    window.addEventListener("offline", disconnected);
    window.addEventListener("online", resumed);
    window.addEventListener("focus", resumed);
    window.addEventListener("pageshow", resumed);
    document.addEventListener("visibilitychange", visible);
    validate();
    return () => {
      active = false;
      epoch++;
      window.clearInterval(refresh);
      window.removeEventListener("offline", disconnected);
      window.removeEventListener("online", resumed);
      window.removeEventListener("focus", resumed);
      window.removeEventListener("pageshow", resumed);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [auth, uid, orgId, retryNonce, enabled]);

  return {validation, online, error, retry: () => {setError(false); setRetryNonce(value => value + 1);}};
}
const verificationSession = createLiveSessionScope(useTokenVerification);
export const AttendanceVerificationProvider = verificationSession.Provider;
export function useAttendanceAuthorization(auth, permissions) {
  const uid = permissions.uid || "";
  const orgId = permissions.orgId || "";
  const permitted = permissions.isOwner === true ||
    ["owner", "admin", "attendance_admin", "pastor", "shepherd"].includes(permissions.role) ||
    ["canManageAttendance", "canViewReports", "canManagePeople"].some(name => permissions.can?.(name));
  const shared = verificationSession.useShared(uid + ":" + orgId, [auth, uid, orgId], Boolean(uid && orgId));
  const local = useTokenVerification(auth, uid, orgId, !shared.provided);
  const {validation, online, error, retry} = shared.provided
    ? shared.value || {validation: null, online: navigator.onLine !== false, error: false, retry: () => {}}
    : local;
  useEffect(() => {
    if (permissions.authorizationStatus === "denied" ||
        (permissions.authorizationStatus === "allowed" && !permitted)) {
      try {
        Object.keys(localStorage).filter(isSensitiveCacheKey).forEach(key => localStorage.removeItem(key));
      } catch { /* Protected content remains hidden if storage is unavailable. */ }
    }
  }, [permissions.authorizationStatus, permitted]);
  const fresh = online && navigator.onLine !== false &&
    permissions.authorizationStatus === "allowed" && permitted &&
    permissions.serverConfirmed === true && auth.currentUser?.uid === uid && Boolean(uid && orgId) &&
    validation?.uid === uid && validation?.orgId === orgId &&
    Date.now() - validation.at < ATTENDANCE_AUTH_MAX_AGE_MS;
  const denied = permissions.authorizationStatus === "denied" ||
    (permissions.authorizationStatus === "allowed" && !permitted);
  return {
    fresh,
    status: fresh ? "allowed" : denied ? "denied" : !online || navigator.onLine === false ? "offline" :
      error || permissions.authorizationStatus === "error" ? "error" : "checking",
    // Retry renews both halves: the sign-in token and the role listeners.
    retry: () => { retry(); permissions.retry?.(); },
  };
}

export function useAttendanceAuthorizationFreshness(auth, permissions) {
  return useAttendanceAuthorization(auth, permissions).fresh;
}
